import { API_URL } from "env"; // Đảm bảo đường dẫn import đúng

// ==========================================
// 1. DEFINITIONS (Type & Interface)
// ==========================================

// Interface cho Flash Sale
export interface PromotionFlashSale {
    product_id: number;
    percent: number;
    promotion_flash_sale?: {
        name: string;
        start_time: string;
        end_time: string;
    } | null;
}

// Interface chi tiết sản phẩm
export interface ProductDetailData {
    id: number;
    name: string;
    price: number;
    slug: string;
    discount?: number;
    discount_price?: number | string | null;
    avatar: string;
    list_thumbnail: string; // Chuỗi JSON stringified
    promotion?: {
        type: string;
        percent: number;
        detail?: {
            id: number;
            name: string;
            slug: string;
            start_date: string;
            end_date: string;
            is_active: number;
            created_at: string;
            updated_at: string;
        } | null;
    } | null;
    promotion_product?: Array<{
        product_id: number;
        promotion_id: number;
        percent: number;
        promotion?: {
            id: number;
            name: string;
            slug: string;
            start_date: string;
            end_date: string;
            is_active: number;
            created_at?: string;
            updated_at?: string;
        } | null;
    }>;
    product_category: {
        id: number;
        name: string;
        slug?: string;
    };
    promotion_flash_sale_product: PromotionFlashSale[];
    content?: string;
    ingredients?: string;
    indications?: string;
    contraindications?: string;
    dosage?: string;
    drug_interactions?: string;
    storage?: string;
    overdose?: string;
    seo_page?: string;
    customer_like?: CustomerLike[];
}

// Interface Response chuẩn từ API cho chi tiết sản phẩm
export interface ProductDetailResponse {
    status: boolean;
    data: ProductDetailData;
    msg: string;
}

export interface CustomerLike {
    id: number;
    product_id: number;
    customer_id: number;
    is_like: number;
}

function parsePriceValue(value?: number | string | null): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const normalized = value.replace(/\./g, '').replace(/,/g, '.').trim();
        const parsed = parseFloat(normalized);

        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return undefined;
}

function normalizeProductDetailData(product: ProductDetailData): ProductDetailData {
    const originalPrice = parsePriceValue(product.price) || 0;
    const apiDiscountPrice = parsePriceValue(product.discount_price);

    const computedDiscountPrice = apiDiscountPrice !== undefined
        ? apiDiscountPrice
        : (product.promotion?.percent ? Math.round(originalPrice * (1 - product.promotion.percent / 100)) : undefined);

    const discountPrice = computedDiscountPrice !== undefined && computedDiscountPrice < originalPrice
        ? computedDiscountPrice
        : undefined;

    const discountPercent = discountPrice !== undefined && originalPrice > 0
        ? Math.round((1 - discountPrice / originalPrice) * 100)
        : (product.promotion?.percent || product.discount || 0);

    const promotionProducts = product.promotion_product && product.promotion_product.length > 0
        ? product.promotion_product
        : (product.promotion?.type === 'PROMOTION' && product.promotion.detail
            ? [{
                product_id: product.id,
                promotion_id: product.promotion.detail.id,
                percent: product.promotion.percent || discountPercent,
                promotion: {
                    id: product.promotion.detail.id,
                    name: product.promotion.detail.name,
                    slug: product.promotion.detail.slug,
                    start_date: product.promotion.detail.start_date,
                    end_date: product.promotion.detail.end_date,
                    is_active: product.promotion.detail.is_active,
                    created_at: product.promotion.detail.created_at,
                    updated_at: product.promotion.detail.updated_at,
                },
            }]
            : []);

    return {
        ...product,
        price: originalPrice,
        discount: discountPercent,
        discount_price: discountPrice,
        promotion_product: promotionProducts,
        promotion_flash_sale_product: product.promotion_flash_sale_product || [],
    };
}

async function fetchProductDetail(slug: string, userId?: number | string): Promise<ProductDetailResponse | null> {
    // 1. Validate slug
    if (!slug) {
        console.error("fetchProductDetail: Slug is required");
        return null;
    }

    const query = new URLSearchParams();
    if (userId) {
        query.append('customer_id_like', String(userId));
    }

    // 2. Construct URL: {{link}}/api/v1/fe/product/:slug
    const baseUrl = `${API_URL.replace(/\/$/, '')}/api/v1/fe/product/${slug}`;
    const url = `${baseUrl}?${query.toString()}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                // 'Authorization': `Bearer ${token}` 
            },
        });

        if (!response.ok) {
            // Nếu 404 thì return null để UI biết hiển thị trang Not Found
            if (response.status === 404) return null;
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ProductDetailResponse = await response.json();

        if (!data?.data) {
            return data;
        }

        return {
            ...data,
            data: normalizeProductDetailData(data.data),
        };

    } catch (error) {
        console.error(`Failed to fetch product detail [${slug}]:`, error);
        return null;
    }
}

export async function fetchRelatedProducts(categoryId: number) {
    // 1. Validate
    if (!categoryId) {
        console.error("fetchRelatedProducts: categoryId is required");
        return null;
    }

    // 2. Construct URLSearchParams
    const query = new URLSearchParams();
    query.append('sort[]', 'created_at,desc');
    query.append('product_category_id', String(categoryId));
    query.append('limit', '6');

    // 3. Construct URL
    const url = `${API_URL.replace(/\/$/, '')}/api/v1/fe/products?${query.toString()}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                // 'Authorization': `Bearer ${token}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;

    } catch (error) {
        console.error(`Failed to fetch related products for category [${categoryId}]:`, error);
        return null;
    }
}

export async function sendPriceAssessment(productId: number, isLike: number) {
    const url = `${API_URL.replace(/\/$/, '')}/api/v1/fe/customer_like`;
    const token = localStorage.getItem('token');

    if (!token) return { status: false, msg: "Vui lòng đăng nhập để đánh giá" };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                product_id: productId,
                is_like: isLike
            })
        });

        return await response.json();
    } catch (error) {
        console.error("Lỗi đánh giá:", error);
        return { status: false, msg: "Lỗi kết nối" };
    }
}

// ==========================================
// 3. EXPORTS
// ==========================================

export {
    fetchProductDetail,
};